import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  Input,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { KENDO_TEXTAREA } from '@progress/kendo-angular-inputs';

function setEnabledState(formControl: AbstractControl<unknown>, enabled: boolean): void {
  if (enabled) {
    formControl.enable();
  } else {
    formControl.disable();
  }
}

type TextareaForm = FormGroup<{ textarea: FormControl<string> }>;

interface TextareaFormValue {
  textarea: string;
}

// TODO: Move the component to `Common.UI` repository.
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, KENDO_TEXTAREA],
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line no-use-before-define
      useExisting: forwardRef(() => TextareaComponent)
    }
  ],
  selector: 'aos-textarea',
  styleUrl: './textarea.component.scss',
  templateUrl: './textarea.component.html'
})
export class TextareaComponent implements ControlValueAccessor, OnInit {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly formBuilder = inject(FormBuilder);

  @Input()
  maxLength = Infinity;

  @Input()
  set value(value: string) {
    this.valueInternal = value;
    this.form.controls.textarea.setValue(value);
  }

  get value(): string {
    return this.valueInternal;
  }

  protected form!: TextareaForm;
  protected maxLengthCounterIsVisible = false;
  protected maxLengthCounterText = '';

  private readonly destroyRef = inject(DestroyRef);
  private onChangeInternal = (_value: string): void => {};
  private onTouchedInternal = (): void => {};
  private valueInternal = '';

  ngOnInit(): void {
    this.createForm();
    this.prepareMaxLengthCounter();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChangeInternal = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedInternal = fn;
  }

  setDisabledState(disabled: boolean): void {
    setEnabledState(this.form, !disabled);
  }

  writeValue(value: string): void {
    this.form.controls.textarea.setValue(value);
    this.changeDetectorRef.markForCheck();
  }

  protected textareaBlurHandler(): void {
    const { textarea } = this.form.controls;
    textarea.setValue(textarea.value.trim());
    this.onTouchedInternal();
  }

  private createForm(): void {
    this.form = this.formBuilder.group<TextareaFormValue>({
      textarea: this.valueInternal
    }) as TextareaForm;

    this.form.controls.textarea.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string): void => {
        if (this.valueInternal === value) return;
        this.valueInternal = value;
        this.onChangeInternal(value);
      });
  }

  private prepareMaxLengthCounter(): void {
    this.maxLengthCounterIsVisible = this.maxLength !== Infinity;
    if (!this.maxLengthCounterIsVisible) return;
    this.form.controls.textarea.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | null): void => {
        const text = value ?? '';
        this.maxLengthCounterText = `${text.length} / ${this.maxLength}`;
        this.changeDetectorRef.markForCheck();
      });
  }
}
